import React, { useEffect, useMemo, useState } from "react";

import PortfolioAccountPreviewModal from "./portfolio-overview/PortfolioAccountPreviewModal";
import PortfolioOverviewTab from "./portfolio-overview/PortfolioOverviewTab";
import RelationshipAccountsLoader from "./RelationshipAccountsLoader";
import { useRelationshipAccountsFetch } from "./useRelationshipAccountsFetch";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchMyPortfolioOverview } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import type { RelationshipAccountSummary } from "../../Store/Reducers/RelationshipSlice";
import {
  isAccountTeamRole,
  isRole,
  ROLES,
} from "../../Utils/Constants/RoleConstants";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";

import "./account-relationships/relationship-prototype-skin.css";

interface PortfolioOverviewShellProps {
  onNavigateToAccountRelationships?: (
    account: RelationshipAccountSummary
  ) => void;
  portfolioGridOwnerLabel?: string;
  /** Ring thickness (Primary/Secondary/Executive) only when My Portfolio is on. */
  myPortfolioActive?: boolean;
}

const PortfolioOverviewShell: React.FC<PortfolioOverviewShellProps> = ({
  onNavigateToAccountRelationships,
  portfolioGridOwnerLabel,
  myPortfolioActive = false,
}) => {
  const dispatch = useAppDispatch();
  const { accounts, error, isScopeLoading } = useRelationshipAccountsFetch();
  const myPortfolioAccounts = useAppSelector(
    state => state.myPortfolioOverview?.data?.accounts
  );

  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  /** Hide Primary/Secondary/Executive ring legend for Account Team only. */
  const showSponsorRoleLegend =
    myPortfolioActive && !isAccountTeamRole(loggedInUser?.role);
  /** Executive login: all bubbles use dashed Executive ring (not P/S assignment). */
  const forceExecutiveRing =
    myPortfolioActive && isRole(loggedInUser?.role, ROLES.EXECUTIVE);

  const [previewAccount, setPreviewAccount] =
    useState<RelationshipAccountSummary | null>(null);

  useEffect(() => {
    if (!myPortfolioActive) return;
    if (forceExecutiveRing || !showSponsorRoleLegend) return;
    if (myPortfolioAccounts?.length) return;
    dispatch(fetchMyPortfolioOverview());
  }, [
    dispatch,
    forceExecutiveRing,
    myPortfolioAccounts?.length,
    myPortfolioActive,
    showSponsorRoleLegend,
  ]);

  const gridAccounts = useMemo(() => {
    if (!myPortfolioActive) return accounts;
    const roleById = new Map(
      (myPortfolioAccounts || []).map(a => [
        String(a.customer_id),
        a.role_type || null,
      ])
    );
    return accounts.map(account => ({
      ...account,
      roleType:
        (account as { roleType?: string | null }).roleType ||
        roleById.get(String(account.id)) ||
        null,
    }));
  }, [accounts, myPortfolioAccounts, myPortfolioActive]);

  return (
    <div className="rl-prototype-skin">
      {isScopeLoading ? (
        <RelationshipAccountsLoader label="Loading portfolio overview…" />
      ) : error && !accounts.length ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          {error}
        </div>
      ) : (
        <PortfolioOverviewTab
          accounts={gridAccounts}
          onOpenAccountPreview={setPreviewAccount}
          showMyRelationships={false}
          portfolioGridOwnerLabel={portfolioGridOwnerLabel}
          showSponsorRoleLegend={showSponsorRoleLegend}
          forceExecutiveRing={forceExecutiveRing}
        />
      )}

      <PortfolioAccountPreviewModal
        open={Boolean(previewAccount)}
        account={previewAccount}
        showMyRelationships={false}
        onClose={() => setPreviewAccount(null)}
        onGoAccountRelationships={() => {
          if (!previewAccount) return;
          onNavigateToAccountRelationships?.(previewAccount);
          setPreviewAccount(null);
        }}
      />
    </div>
  );
};

export default PortfolioOverviewShell;
